import Icon from '../../../../components/tailwind/Icon';
import React, { memo } from 'react';
import rticLogoIcon from '../../images/rtic-logo.svg';
import { formatCurrency } from '../../../../utils/formatters.util';
import { Button } from '../../../../components/tailwind';
import classNames from 'classnames';
// @ts-ignore
import { Element } from '@c2s-npm/plumreact';

const ELEMENTS_COUNT = 5;

type Props = {
  faceName: string;
  showTitleIcon?: boolean;
  totalPrice: number;
  onAddPersonalizationClick?: () => void;
  elements: any[];
  isGroupOrder: boolean;
  quantity: number;
  onEditElement: (faceName: string, elementId: string) => void;
  onRemoveElement: (faceName: string, elementId: string) => void;
  elementPrice: number;
  showAllGroupOrderElements?: boolean;
  onSummaryModalOpen?: () => void;
};

const PersonalizationItem = ({
  faceName,
  showTitleIcon,
  totalPrice,
  onAddPersonalizationClick = () => {},
  onSummaryModalOpen = () => {},
  isGroupOrder,
  quantity,
  elements,
  onEditElement,
  onRemoveElement,
  elementPrice,
  showAllGroupOrderElements = false,
}: Props) => {
  return (
    <div>
      <Header {...{ faceName, showTitleIcon, totalPrice, onAddPersonalizationClick }} />
      <Elements
        {...{
          elements,
          onEditElement,
          elementPrice,
          faceName,
          onRemoveElement,
          isGroupOrder,
          quantity,
          showAllGroupOrderElements,
          onSummaryModalOpen,
        }}
      />
    </div>
  );
};

export default memo(PersonalizationItem);

const Header = ({ showTitleIcon, faceName, totalPrice, onAddPersonalizationClick }) => (
  <div
    className={classNames('tw-flex tw-items-center tw-justify-between tw-gap-3 tw-py-3', {
      'tw-border-b tw-border-b-gray-300': totalPrice > 0,
    })}>
    <div className='tw-inline-flex tw-items-center tw-gap-2'>
      {showTitleIcon && <Icon url={rticLogoIcon} color='#1f1f1f' className='tw-text-body' size={24} />}
      <span className='tw-text-sm tw-font-bold tw-uppercase'>{faceName}</span>
    </div>
    <div className='tw-text-sm tw-font-bold tw-text-brand-black'>
      {totalPrice > 0 ? (
        <span>{formatCurrency(totalPrice)}</span>
      ) : (
        <span
          onClick={onAddPersonalizationClick}
          className='tw-cursor-pointer tw-font-normal tw-uppercase tw-text-brand-black tw-underline'>
          Add Personalization
        </span>
      )}
    </div>
  </div>
);

const TemplateNames = {
  font: 'Name',
  title: 'Name & Title',
  jersey: 'Jersey',
  monogram: 'Monogram',
};
const templateName = (elementType) => {
  return TemplateNames[elementType] || 'Unknown';
};

const Elements = ({
  elements,
  onEditElement,
  onRemoveElement,
  elementPrice,
  faceName,
  isGroupOrder,
  quantity,
  showAllGroupOrderElements,
  onSummaryModalOpen,
}) => {
  return (elements ?? []).slice(0, ELEMENTS_COUNT).map((e) => (
    <div key={e.id} className='tw-flex tw-w-full tw-items-center tw-justify-between tw-py-3'>
      <div className='tw-inline-flex tw-w-full tw-items-center tw-gap-2'>
        <div className='tw-w-full'>
          {!isGroupOrder && <Element value={e} />}

          {isGroupOrder && (
            <div className='tw-mb-5 tw-block tw-w-full last:tw-mb-0'>
              <div className='tw-mb-3 tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-2'>
                <p className='tw-mb-0 tw-text-base tw-font-medium tw-text-brand-black'>
                  {e.isGroup ? <>Template: {templateName(e.elementType)}</> : <>Repeating Design</>}
                </p>
                <ItemActions {...{ onEditElement, onRemoveElement, faceName, element: e }} />
              </div>

              {e.isGroup && (
                <>
                  <div
                    className={classNames({
                      'tw-flex tw-flex-col tw-items-center tw-gap-3': !showAllGroupOrderElements,
                      'tw-grid tw-grid-cols-1 tw-items-center tw-justify-between tw-gap-3 md:tw-grid-cols-2 lg:tw-grid-cols-3':
                        showAllGroupOrderElements,
                    })}>
                    {e.data
                      .slice(0, showAllGroupOrderElements ? e.data.length : ELEMENTS_COUNT)
                      .map((d, i) => (
                        <div className='tw-w-full' key={i}>
                          <Element value={e} groupIndex={i} />
                        </div>
                      ))}
                  </div>

                  {!showAllGroupOrderElements && e.data.length > ELEMENTS_COUNT && (
                    <div className='mt-4'>
                      <Button
                        onClick={onSummaryModalOpen}
                        variant='link-body'
                        noPadding
                        className='tw-font-bold'
                        size='lg'>
                        Show All
                      </Button>
                    </div>
                  )}
                </>
              )}

              {!e.isGroup && <Element value={e} />}
            </div>
          )}
        </div>
        {['uploaded', 'file'].includes(e.elementType) && (
          <span className='tw-text-sm tw-font-normal tw-text-gray-dark'>
            +{formatCurrency(elementPrice * quantity)}
          </span>
        )}
        {e.fee && (
          <span className='tw-text-muted tw-text-sm tw-font-normal'>
            +{formatCurrency(e.fee.rate * quantity)}
          </span>
        )}
      </div>

      {!isGroupOrder && <ItemActions {...{ onEditElement, onRemoveElement, faceName, element: e }} />}
    </div>
  ));
};

const ItemActions = ({ onEditElement, onRemoveElement, faceName, element }) => {
  return (
    <div className='tw-min-w-[120px] tw-text-right'>
      <Button noPadding variant='link' onClick={onEditElement(faceName, element.id)}>
        EDIT
      </Button>{' '}
      <span className='tw-mx-1'>|</span>{' '}
      <Button noPadding variant='link' onClick={onRemoveElement(faceName, element.id)}>
        DELETE
      </Button>
    </div>
  );
};
